/**
 * Streaks, badges and levels (docs/streaks-badges-and-levels.md). The server decides every badge
 * and level; these helpers only turn its rows and state into what the pop-ups and the
 * Achievements page draw. Badge art lives in /public/achievements (generated by
 * docs/achievements/make_badges.js).
 */
export const streakBadgeUrl = (day) => `/achievements/streak/streak_${day}.svg`;
export const levelBadgeUrl = (level) => `/achievements/level/level_${String(level).padStart(2, '0')}.svg`;

export const dayOfStreakKey = (key) => {
  const m = /^st(\d+)$/.exec(key || '');
  return m ? Number(m[1]) : null;
};

export const bracketFor = (level, brackets) => (brackets || []).find((b) => level >= b.from && level <= b.to) || null;

/**
 * Progress to the next level; null at the top level. Measured from the threshold the lifetime has
 * actually reached when that is below the stored level (thresholds raised after the child got
 * there, levels plan §5.4), so `toGo` is always the real distance to the next level.
 */
export function levelProgress(lifetime, level, thresholds) {
  const n = Number(lifetime) || 0;
  const reached = Math.max(1, (thresholds || []).filter((t) => n >= t).length);
  const from = thresholds?.[Math.min(level, reached) - 1];
  const to = thresholds?.[level];
  if (from == null || to == null) return null;
  const into = Math.max(0, Math.min(to - from, n - from));
  return { into, span: to - from, toGo: to - from - into, pct: Math.round((into / (to - from)) * 100) };
}

/**
 * One action can queue several cards; they show in this order (levels plan §7): a freeze that
 * saved the streak while the child was away, today's streak, a freeze earned, a streak badge, a level.
 */
const ORDER = { 'freeze-saved': 0, 'streak-day': 1, 'freeze-earned': 2, 'streak-badge': 3, level: 4, 'topic-level-up': 5 };

/** Celebration cards for the award rows of one reply (or the drained pending queue). */
export function celebrationsFromRows(rows) {
  const out = [];
  let topic = null;
  for (const r of Array.isArray(rows) ? rows : []) {
    if (!r) continue;
    if (r.event === 'streak.freeze_saved') out.push({ type: 'freeze-saved', freezes: r.freezes ?? 1, label: r.label });
    else if (r.event === 'streak.freeze_earned') out.push({ type: 'freeze-earned', freezes: r.freezes ?? 1 });
    else if (r.event === 'streak.milestone' && dayOfStreakKey(r.key)) out.push({ type: 'streak-badge', key: r.key, day: dayOfStreakKey(r.key), amount: r.amount });
    else if (r.event === 'level.up' && r.level) out.push({ type: 'level', from: r.level.from, to: r.level.to });
    else if (r.event === 'lesson.level_up') {
      // Red → green pays two rows (key amber, then green); the child sees one card for the colour reached.
      if (!topic) out.push((topic = { type: 'topic-level-up', to: r.key, amount: 0 }));
      if (r.key === 'green') topic.to = 'green';
      topic.amount += Number(r.amount) || 0;
    }
  }
  return out;
}

/**
 * Adds cards in celebration order. The card on screen (`headShown`) never jumps; a card still
 * held back (a level-up earned mid-quiz) is re-sorted with the newcomers.
 */
export function enqueueCelebrations(queue, cards, headShown = false) {
  if (!cards.length) return queue;
  const [head, ...rest] = headShown ? queue : [null, ...queue];
  const sorted = [...rest, ...cards].map((c, i) => ({ c, i })).sort((a, b) => ORDER[a.c.type] - ORDER[b.c.type] || a.i - b.i).map((x) => x.c);
  return head ? [head, ...sorted] : sorted;
}
